/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ResourceKeyCell } from 'src/appShell/globalHeader/globalHeaderBlades/settingsBlade/ResourceKeyCell';
import { CopyButton } from 'src/core/components/CopyButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logDemoButtonClick, logFeaturesResouceSelection, logNextSteps } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { AzureResourcesManager } from 'src/modules/azure/components/AzureResourcesManager';
import { useAzureResourcesManagement } from 'src/modules/azure/hooks/useAzureResourcesManagement';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';

type NextStepsGetStartedScreenProps = {
    tryoutName: TelemetryFeatureTitle;
    samplesLink: string;
    isResultsEmpty: boolean;
    documentationLink: string;
};

const messages = defineMessages({
    or: { id: 'languageStudio.nextStepsGetStartedScreen.or', defaultMessage: 'or' },
    regionLabel: { id: 'languageStudio.nextStepsGetStartedScreen.regionLabel', defaultMessage: 'Region:' },
    signInAzure: { id: 'languageStudio.nextStepsGetStartedScreen.signInAzure', defaultMessage: 'Sign in with Azure' },
    endpointLabel: { id: 'languageStudio.nextStepsGetStartedScreen.endpointLabel', defaultMessage: 'Language endpoint' },
    viewSamples: { id: 'languageStudio.nextStepsGetStartedScreen.viewSamples', defaultMessage: 'View samples on GitHub' },
    quickStart: { id: 'languageStudio.nextStepsGetStartedScreen.quickStart', defaultMessage: '2. Follow the quick start' },
    openQuickStart: { id: 'languageStudio.nextStepsGetStartedScreen.openQuickStart', defaultMessage: 'Open the quick start' },
    signUpAzure: { id: 'languageStudio.nextStepsGetStartedScreen.signUpAzure', defaultMessage: 'Sign up for an Azure account' },
    createResource: { id: 'languageStudio.nextStepsGetStartedScreen.createResource', defaultMessage: 'Create a Language resource' },
    resourceLabel: { id: 'languageStudio.nextStepsGetStartedScreen.resourceLabel', defaultMessage: 'Resource for Language service' },
    subscriptionKeyLabel: { id: 'languageStudio.nextStepsGetStartedScreen.subscriptionKeyLabel', defaultMessage: 'Subscription key:' },
    createLanguageResource: {
        id: 'languageStudio.nextStepsGetStartedScreen.createLanguageResource',
        defaultMessage: '1. Create a Language resource'
    },
    quickStartDesc: {
        id: 'languageStudio.nextStepsGetStartedScreen.quickStartDesc',
        defaultMessage: `Once you have created a Language resource, next set up the environment,
        install the Language SDK, and run sample code by following the steps in the quick start guide.`
    },
    createLanguageResourceDesc: {
        id: 'languageStudio.nextStepsGetStartedScreen.createLanguageResourceDesc',
        defaultMessage: 'To use {tryoutName}, you’ll need an Azure account with a paid Language or Cognitive Services resource.'
    }
});

const theme = getScopedTheme('DemoPage');
const textStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { marginLeft: 20 } });

export const NextStepsGetStartedScreen = observer((props: NextStepsGetStartedScreenProps) => {
    const { tryoutName, samplesLink, isResultsEmpty, documentationLink } = props;
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const changeCsAccountTracker = useProgressTracker();
    const { isSignedIn } = rootStore.authStore;
    const { csAccountStore, subscriptionId } = rootStore.azureStore.session;
    const { resourcesTracker, resources } = useAzureResourcesManagement({
        subscriptionId,
        resourceStrategy: AzureResourcesStrategies.languageStudioResourceStrategy
    });

    const [isCreateCsAccountModalHidden, setIsCreateCsAccountModalHidden] = React.useState(true);

    const languageResourcesOptions: IDropdownOption[] = resources.map(r => ({
        key: r.id,
        text: r.name,
        data: { secondaryText: `(${r.region}, ${r.sku.code})` }
    }));

    const handleClickLink = () => {
        if (!isResultsEmpty) {
            logNextSteps();
        }
    };

    const handleSignInClick = () => {
        logDemoButtonClick(tryoutName, 'Nextsteps:GetStarted', 'SignIn');
        dispatcher.dispatch('login');
    };

    const handleCustomButtonClick = (buttonName: string) => {
        logDemoButtonClick(tryoutName, 'Nextsteps:GetStarted', buttonName);
        handleClickLink();
    };

    return (
        <Stack tokens={{ childrenGap: 25 }}>
            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText variant="mediumPlus" as="h3" fontWeight={600}>
                    {localize(messages.createLanguageResource)}
                </LuisText>
                <LuisText styles={textStyles}>{localize(messages.createLanguageResourceDesc, { tryoutName })}</LuisText>
            </Stack>

            <Stack tokens={{ childrenGap: 10 }}>
                <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 10, padding: '0 0 0 20px' }}>
                    {isSignedIn
                        ? [
                              <LuisDropdown
                                  selectedKey={csAccountStore?.id}
                                  options={languageResourcesOptions}
                                  label={localize(messages.resourceLabel)}
                                  styles={{ dropdown: { minWidth: '300px' } }}
                                  isLoading={resourcesTracker.isRunning || changeCsAccountTracker.isRunning}
                                  onChange={(_, option) => {
                                      logFeaturesResouceSelection(tryoutName, option.key as string, 'Nextsteps:GetStarted');
                                      dispatcher.dispatch('switchAzureCsAccountInSession', {
                                          toastData: {},
                                          newAccount: false,
                                          tracker: changeCsAccountTracker,
                                          csAccountStore: resources.find(r => r.id === option.key)
                                      });
                                  }}
                              />,
                              <ActionButton
                                  text={localize(messages.createResource)}
                                  styles={{ label: { color: theme.primaryColor } }}
                                  onClick={() => {
                                      logDemoButtonClick(tryoutName, 'Nextsteps:GetStarted', 'CreateResource');
                                      setIsCreateCsAccountModalHidden(false);
                                  }}
                              />,
                              <AzureResourcesManager
                                  serviceTarget={'TextAnalytics'}
                                  isCreateResourceVisible={!isCreateCsAccountModalHidden}
                                  onChangeCreateResourceVisible={isVisible => setIsCreateCsAccountModalHidden(!isVisible)}
                              />
                          ]
                        : [
                              <PrimaryButton onClick={handleSignInClick}>{localize(messages.signInAzure)}</PrimaryButton>,
                              <LuisText>{localize(messages.or)}</LuisText>,
                              <ExternalLink href="https://aka.ms/azure-freeaccount" onClick={() => handleCustomButtonClick('SignUp')}>
                                  {localize(messages.signUpAzure)}
                              </ExternalLink>
                          ]}
                </Stack>

                {csAccountStore?.id && (
                    <Stack tokens={{ childrenGap: 5, padding: '0 0 0 20px' }}>
                        <Stack horizontal tokens={{ childrenGap: 60 }}>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                                <LuisText fontWeight={600}>{localize(messages.regionLabel)}</LuisText>
                                <LuisText>{csAccountStore.region}</LuisText>
                                <CopyButton label={csAccountStore.region} textToCopy={csAccountStore.region} />
                            </Stack>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                                <LuisText fontWeight={600}>{localize(messages.subscriptionKeyLabel)}</LuisText>
                                <ResourceKeyCell account={csAccountStore} forceDisplayCopyButton />
                            </Stack>
                        </Stack>

                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                            <LuisText fontWeight={600}>{localize(messages.endpointLabel)}</LuisText>
                            <LuisText>{csAccountStore.endpoint}</LuisText>
                            <CopyButton label={csAccountStore.endpoint} textToCopy={csAccountStore.endpoint} />
                        </Stack>
                    </Stack>
                )}
            </Stack>

            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText variant="mediumPlus" as="h3" fontWeight={600}>
                    {localize(messages.quickStart)}
                </LuisText>
                <LuisText styles={textStyles}>{localize(messages.quickStartDesc)}</LuisText>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 10, padding: '0 0 0 20px' }}>
                <PrimaryButton href={documentationLink} target="_blank" onClick={() => handleCustomButtonClick('QuickStart')}>
                    {localize(messages.openQuickStart)}
                </PrimaryButton>
                <DefaultButton
                    target="_blank"
                    href={samplesLink}
                    iconProps={{ iconName: 'Github' }}
                    text={localize(messages.viewSamples)}
                    onClick={() => handleCustomButtonClick('GithubSamples')}
                />
            </Stack>
        </Stack>
    );
});
